import { type RoomSnapshot, type Seat } from '@euchre/core';
import { feedbackButton } from './extras.js';
import { h } from '../dom.js';
import { app, rerender, send, toast } from '../state.js';
import { openProfile } from './profile.js';
import { auth } from './account.js';
import { rememberRules } from './home.js';
import { rulesEditor, rulesSummary } from './rules.js';

const SEAT_SPOT = ['spot-south', 'spot-west', 'spot-north', 'spot-east'];

export function inviteLink(code: string): string {
  return `${location.origin}/r/${code}`;
}

async function copyInvite(code: string): Promise<void> {
  const link = inviteLink(code);
  try {
    await navigator.clipboard.writeText(link);
    toast('Invite link copied. Paste it into a text or email to your friends.');
  } catch {
    toast(`Share this link: ${link}`, 8000);
  }
}

function seatCard(room: RoomSnapshot, seat: Seat): HTMLElement {
  const info = room.seats[seat]!;
  const o = info.occupant;
  const isHost = room.you.isHost;
  const mine = room.you.seat === seat;
  const team = seat % 2;
  const partnerOfMe = room.you.seat !== null && !mine && room.you.seat % 2 === team;
  const label = mine ? 'Your seat' : partnerOfMe ? 'Your partner' : room.you.seat !== null ? 'Opponent' : `Seat ${seat + 1}`;
  const children: (HTMLElement | string | null)[] = [h('span', { class: 'seat-label', text: label })];

  if (!o) {
    children.push(h('span', { class: 'seat-name empty', text: 'Empty chair' }));
    const actions = h('div', { class: 'seat-actions' });
    actions.append(h('button', { class: 'btn small', type: 'button', text: room.you.seat === null ? 'Sit here' : 'Move here', onclick: () => send({ type: 'sit', seat }) }));
    if (isHost) actions.append(h('button', { class: 'btn small quiet', type: 'button', text: 'Add a bot', onclick: () => send({ type: 'add_bot', seat }) }));
    children.push(actions);
  } else if (o.kind === 'bot') {
    children.push(h('span', { class: 'seat-name', text: o.name }), h('span', { class: 'seat-status', text: 'Bot, always ready' }));
    if (isHost) children.push(h('div', { class: 'seat-actions' }, h('button', { class: 'btn small quiet', type: 'button', text: 'Remove bot', onclick: () => send({ type: 'remove_bot', seat }) })));
  } else {
    const hostSeat = o.playerId === room.hostId;
    // The host doesn't need an "I'm ready" step: dealing the first hand says they're ready.
    const status = !o.connected ? 'Disconnected' : hostSeat ? 'Host' : o.ready ? 'Ready' : 'Not ready yet';
    children.push(
      h('span', { class: 'seat-name', text: mine ? `${o.name} (you)` : o.name }),
      h('span', { class: `seat-status ${(o.ready || hostSeat) && o.connected ? 'ok' : ''}`, text: status }),
      h('button', {
        class: 'link-btn seat-stats',
        type: 'button',
        text: mine ? 'My stats' : 'Stats',
        onclick: () => openProfile(mine ? undefined : o.playerId),
      }),
      // The admin (David) can give someone with an unsuitable name a plain one.
      auth.account?.admin && !mine
        ? h('button', {
            class: 'link-btn seat-stats',
            type: 'button',
            text: 'Reset name',
            onclick: () => {
              if (window.confirm(`Give ${o.name} a plain name like “Player 4821”?`)) send({ type: 'admin_reset_name', playerId: o.playerId });
            },
          })
        : null,
    );
    if (mine) {
      children.push(
        h(
          'div',
          { class: 'seat-actions' },
          hostSeat
            ? null
            : h('button', {
                class: `btn small ${o.ready ? 'quiet' : 'primary'}`,
                type: 'button',
                text: o.ready ? 'Not ready' : "I'm ready",
                onclick: () => send({ type: 'set_ready', ready: !o.ready }),
              }),
          h('button', { class: 'btn small quiet', type: 'button', text: 'Stand up', onclick: () => send({ type: 'stand' }) }),
        ),
      );
    }
  }
  return h('div', { class: `seat-card ${SEAT_SPOT[seat]} team-${team}${mine ? ' mine' : ''}` }, children);
}

function rulesPanel(room: RoomSnapshot): HTMLElement {
  const editable = room.you.isHost;
  const open = app.ui.lobbyRulesOpen;
  return h(
    'section',
    { class: 'rules-panel' },
    h(
      'p',
      { class: 'rules-line' },
      h('span', { class: 'rules-label', text: 'Rules: ' }),
      h('span', { text: rulesSummary(room.rules) }),
      ' ',
      h('button', {
        class: 'link-btn',
        type: 'button',
        text: open ? 'Hide' : editable ? 'Change' : 'Details',
        attrs: { 'aria-expanded': String(open) },
        onclick: () => {
          app.ui.lobbyRulesOpen = !open;
          rerender();
        },
      }),
    ),
    open
      ? h(
          'div',
          { class: 'rules-detail' },
          h('p', { class: 'hint', text: editable ? 'Changing a rule asks everyone to ready up again.' : 'The host sets these.' }),
          rulesEditor(room.rules, {
            editable,
            onChange: (patch) => {
              send({ type: 'set_rules', rules: patch });
              rememberRules({ ...room.rules, ...patch });
            },
          }),
        )
      : null,
  );
}

function inviteBox(room: RoomSnapshot, emptySeats: number): HTMLElement | null {
  if (emptySeats === 0) return null;
  return h(
    'section',
    { class: 'invite-box' },
    h('p', { class: 'invite-title', text: emptySeats === 1 ? 'One chair is empty.' : `${emptySeats} chairs are empty.` }),
    h('p', { class: 'hint', text: `Send friends this link, or give them the table code ${room.code}:` }),
    h(
      'div',
      { class: 'invite-row' },
      h('span', { class: 'invite-link', text: inviteLink(room.code) }),
      h('button', { class: 'btn', type: 'button', text: 'Copy invite link', onclick: () => void copyInvite(room.code) }),
    ),
  );
}

/** If the last game here was cancelled (no people left, only bots), say so plainly instead of leaving it in the chat. */
function cancelledNotice(room: RoomSnapshot): HTMLElement | null {
  const sys = room.chat.filter((m) => m.from === null).map((m) => m.text);
  const lastIndex = (prefix: string) => sys.map((t) => t.startsWith(prefix)).lastIndexOf(true);
  const cancelled = lastIndex('Game cancelled');
  const started = lastIndex('Game on!');
  if (cancelled < 0 || cancelled < started) return null;
  return h(
    'div',
    { class: 'cancel-note', attrs: { role: 'status' } },
    h('strong', { text: 'The last game was cancelled.' }),
    ' Nobody was left playing, only bots, so it was stopped. Sit down and deal to start a new game.',
  );
}

function lobbyChatButton(): HTMLElement {
  const unread = app.unreadChat;
  return h(
    'button',
    {
      class: 'btn chat-toggle',
      type: 'button',
      attrs: { 'aria-controls': 'side-panel', 'aria-expanded': String(app.ui.drawerOpen) },
      onclick: () => {
        app.ui.drawerOpen = !app.ui.drawerOpen;
        rerender();
      },
    },
    'Chat',
    unread ? h('span', { class: 'count', text: String(unread), attrs: { 'aria-label': `${unread} new` } }) : null,
  );
}

export function lobbyScreen(room: RoomSnapshot): HTMLElement {
  const empty = room.seats.filter((s) => !s.occupant).map((s) => s.seat);
  const filled = empty.length === 0;
  const hostId = room.hostId;
  const waiting = room.seats
    .map((s) => s.occupant)
    .filter((o) => o?.kind === 'human' && o.playerId !== hostId && (!o.ready || !o.connected))
    .map((o) => (o as { name: string }).name);
  const hostSeated = room.you.isHost && room.you.seat !== null;
  const canStart = filled && waiting.length === 0;
  const reason = room.you.isHost && !hostSeated
    ? 'Sit in a chair to play.'
    : !filled
    ? app.ui.tableMode === 'friends'
      ? 'Waiting for friends to fill the empty chairs.'
      : 'Waiting for friends — or fill the empty chairs with bots.'
    : waiting.length
      ? `Waiting on ${waiting.join(', ')} to say they're ready.`
      : 'Everyone’s here. Deal when you like.';

  const deal = () => {
    // The host saying "deal" means the host is ready.
    if (hostSeated) send({ type: 'set_ready', ready: true });
    send({ type: 'start_game' });
  };

  const hostControls = room.you.isHost
    ? h(
        'div',
        { class: 'start-actions' },
        empty.length
          ? h('button', {
              class: `btn big${app.ui.tableMode === 'friends' ? '' : ' primary'}`,
              type: 'button',
              text: empty.length === 1 ? 'Fill the empty chair with a bot' : `Fill ${empty.length} empty chairs with bots`,
              onclick: () => {
                for (const seat of empty) send({ type: 'add_bot', seat });
              },
            })
          : null,
        h('button', { class: `btn big${filled ? ' primary' : ''}`, type: 'button', text: 'Deal the first hand', disabled: !canStart || !hostSeated, onclick: deal }),
      )
    : h('p', { class: 'hint', text: 'The host deals once everyone is ready.' });

  return h(
    'main',
    { class: 'lobby' },
    h(
      'header',
      { class: 'lobby-head' },
      h('div', null, h('p', { class: 'hint', text: 'Table code' }), h('p', { class: 'room-code', text: room.code })),
      h(
        'div',
        { class: 'lobby-head-actions' },
        lobbyChatButton(),
        feedbackButton('btn'),
        h('button', { class: 'btn quiet', type: 'button', text: 'Leave table', onclick: () => send({ type: 'leave_room' }) }),
      ),
    ),
    cancelledNotice(room),
    inviteBox(room, empty.length),
    h(
      'section',
      { class: 'seat-picker', attrs: { 'aria-label': 'Seats' } },
      h('div', { class: 'mini-table' }, h('p', { text: 'Partners sit across from each other' })),
      ([0, 1, 2, 3] as Seat[]).map((s) => seatCard(room, s)),
    ),
    h('section', { class: 'start-row' }, h('p', { class: 'hint start-reason', text: reason }), hostControls),
    rulesPanel(room),
    room.spectators.length
      ? h('p', { class: 'watchers' }, `Standing by: ${room.spectators.map((s) => (s.playerId === app.playerId ? 'you' : s.name)).join(', ')}`)
      : null,
    app.ui.drawerOpen ? h('div', { class: 'drawer-scrim', onclick: () => ((app.ui.drawerOpen = false), rerender()) }) : null,
  );
}
